import RewardManager from "./RewardManager";

interface FBTAddData {
  id: string;
  quantity: number;
  sections?: string;
  properties?: { [key: string]: string };
}

class FrequentlyBoughtTogether {
  private static readonly MIN_ITEMS_FOR_DISCOUNT = 2;

  private root: HTMLElement;
  private rows: HTMLElement[];
  private totalEl: HTMLElement | null;
  private compareEl: HTMLElement | null;
  private addBtn: HTMLButtonElement | null;
  private bundleDiscount: number;
  private discount2Cents: number;
  private discount3Cents: number;
  private isSubmitting = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.rows = Array.from(
      root.querySelectorAll<HTMLElement>("[data-fbt-product]")
    );
    this.totalEl = root.querySelector<HTMLElement>("[data-fbt-total]");
    this.compareEl = root.querySelector<HTMLElement>("[data-fbt-compare]");
    this.addBtn = root.querySelector<HTMLButtonElement>("[data-fbt-add]");

    const parsedDiscount = parseFloat(root.dataset.bundleDiscount || "0");
    this.bundleDiscount =
      Number.isFinite(parsedDiscount) && parsedDiscount > 0 ? parsedDiscount : 0;

    this.discount2Cents = this.parseCents(root.dataset.discountTwo);
    this.discount3Cents = this.parseCents(root.dataset.discountThree);

    this.init();
  }

  private parseCents(value: string | undefined): number {
    const parsed = parseInt(value || "0", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  private init(): void {
    this.rows.forEach((row) => {
      const checkbox = this.getCheckbox(row);
      checkbox?.addEventListener("change", () => {
        this.updateTotal();
        this.updateButtonState();
      });
    });

    if (this.addBtn) {
      this.addBtn.addEventListener("click", () => this.submit());
    }

    this.updateTotal();
    this.updateButtonState();
  }

  private getCheckbox(row: HTMLElement): HTMLInputElement | null {
    return row.querySelector<HTMLInputElement>("[data-fbt-checkbox]");
  }

  private isChecked(row: HTMLElement): boolean {
    return this.getCheckbox(row)?.checked ?? false;
  }

  private getCheckedRows(): HTMLElement[] {
    return this.rows.filter((row) => this.isChecked(row));
  }

  private qualifiesForDiscount(): boolean {
    return (
      this.getCheckedRows().length >=
      FrequentlyBoughtTogether.MIN_ITEMS_FOR_DISCOUNT
    );
  }

  private perBottleOffCents(count: number): number {
    if (count >= 3) return this.discount3Cents;
    if (count === 2) return this.discount2Cents;
    return 0;
  }

  private formatMoney(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  private getRowPriceCents(row: HTMLElement): number {
    const price = parseInt(row.getAttribute("data-price") || "0", 10);
    return Number.isFinite(price) ? price : 0;
  }

  private updateTotal(): void {
    const checked = this.getCheckedRows();
    const off = this.perBottleOffCents(checked.length);

    let totalCents = 0;
    let compareTotalCents = 0;

    this.rows.forEach((row) => {
      const price = this.getRowPriceCents(row);
      const isChecked = this.isChecked(row);
      const applyDiscount = isChecked && off > 0;
      const discounted = applyDiscount ? Math.max(0, price - off) : price;

      const currentEl = row.querySelector<HTMLElement>("[data-fbt-price]");
      const compareEl = row.querySelector<HTMLElement>(
        "[data-fbt-price-compare]"
      );

      if (currentEl) currentEl.textContent = this.formatMoney(discounted);
      if (compareEl) {
        if (applyDiscount) {
          compareEl.innerHTML = this.formatMoney(price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (isChecked) {
        totalCents += discounted;
        compareTotalCents += price;
      }
    });

    if (this.totalEl) this.totalEl.textContent = this.formatMoney(totalCents);

    if (this.compareEl) {
      if (off > 0 && compareTotalCents > totalCents) {
        this.compareEl.innerHTML = `${this.formatMoney(compareTotalCents)}`;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }
  }

  private updateButtonState(): void {
    if (!this.addBtn) return;
    const hasSelection = this.getCheckedRows().length > 0;
    this.addBtn.disabled = !hasSelection || this.isSubmitting;
  }

  private setLoadingState(isLoading: boolean): void {
    if (!this.addBtn) return;
    this.addBtn.classList.toggle("is-loading", isLoading);
    if (isLoading) {
      this.addBtn.setAttribute("aria-busy", "true");
    } else {
      this.addBtn.removeAttribute("aria-busy");
    }
  }

  private async addItem(
    variantId: string,
    asBundle: boolean,
    itemIndex: number
  ): Promise<void> {
    const data: FBTAddData = {
      id: variantId,
      quantity: 1,
      sections: "ajax-side-cart,ajax-cart-icon",
    };

    if (asBundle) {
      data.properties = {
        byob: "true",
        percentage_discount: this.bundleDiscount.toString(),
        _bundle_item: itemIndex.toString(),
      };
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  private async submit(): Promise<void> {
    if (this.isSubmitting) return;

    const checkedRows = this.getCheckedRows();
    if (checkedRows.length === 0) return;

    const asBundle = this.qualifiesForDiscount();

    this.isSubmitting = true;
    this.setLoadingState(true);
    this.updateButtonState();

    try {
      let counter = 0;
      for (const row of checkedRows) {
        counter++;
        const variantId = row.getAttribute("data-variant-id") || "";
        if (!variantId) continue;

        await this.addItem(variantId, asBundle, counter);

        if (counter < checkedRows.length) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      const sideCart = document.querySelector("side-cart") as any;
      if (sideCart) {
        await RewardManager.evaluateAndUpdateRewards();
        await sideCart.refreshSideCart();
        sideCart.open("fbt-bundle-added");
      }
    } catch (error) {
      console.error("Error adding bundle to cart:", error);
      alert("Error adding bundle to cart. Please try again.");
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
      this.updateButtonState();
    }
  }
}

export function initFrequentlyBoughtTogether(): void {
  const sections = document.querySelectorAll<HTMLElement>(".fbt");
  sections.forEach((section) => new FrequentlyBoughtTogether(section));
}

export default FrequentlyBoughtTogether;
